// src/components/Account/HelpSupport/SafetyCenter.tsx
import React, { useState } from 'react';
 
const C = {
    primary:            '#6a37d4',
    primaryLight:       '#ae8dff',
    primaryContainer:   '#ede8ff',
    secondary:          '#006947',
    secondaryContainer: '#d1fae5',
    tertiary:           '#6f5900',
    tertiaryContainer:  '#fed01b',
    surface:            '#f3f6ff',
    surfaceLow:         '#eaf1ff',
    surfaceHigh:        '#d6e3f9',
    onBackground:       '#252f3c',
    onSurface:          '#252f3c',
    onSurfaceVariant:   '#525c6a',
    outlineVariant:     '#a4aebe',
};
 
const SafetyCenter: React.FC = () => {
    const [activeTab, setActiveTab] = useState<'kids' | 'parents'>('kids');
 
    // ── Inline typography helpers (scoped to this file only) ─────────────────
    const h2 = (text: React.ReactNode) => (
        <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.35rem', fontWeight: '900', color: C.onBackground, margin: '36px 0 12px 0', paddingBottom: '8px', borderBottom: `2px solid ${C.surfaceHigh}` }}>
            {text}
        </h2>
    );
    const h3 = (text: React.ReactNode) => (
        <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.05rem', fontWeight: '800', color: C.primary, margin: '20px 0 8px 0' }}>
            {text}
        </h3>
    );
    const p = (text: React.ReactNode) => (
        <p style={{ fontSize: '0.95rem', lineHeight: '1.8', color: C.onSurface, margin: '0 0 14px 0', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {text}
        </p>
    );
    const li = (text: React.ReactNode, key?: string | number) => (
        <li key={key} style={{ fontSize: '0.95rem', lineHeight: '1.8', color: C.onSurface, marginBottom: '6px' }}>{text}</li>
    );
    const ul = (items: React.ReactNode[]) => (
        <ul style={{ margin: '0 0 16px 0', paddingLeft: '20px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {items}
        </ul>
    );
 
    const card = (children: React.ReactNode, padding = '32px') => (
        <div style={{ background: '#fff', borderRadius: '20px', padding, boxShadow: '0 4px 24px rgba(0,0,0,0.05)', border: `1px solid ${C.surfaceHigh}` }}>
            {children}
        </div>
    );
 
    const infoCard = (children: React.ReactNode, bg: string, border?: string) => (
        <div style={{ background: bg, borderRadius: '14px', padding: '20px 24px', marginBottom: '16px', border: border ? `1.5px solid ${border}` : 'none' }}>
            {children}
        </div>
    );
 
    const tabBtn = (id: 'kids' | 'parents', label: string) => (
        <button
            key={id}
            onClick={() => setActiveTab(id)}
            style={{
                padding: '10px 24px', borderRadius: '10px', border: 'none',
                fontSize: '0.9rem', fontWeight: '700', cursor: 'pointer',
                fontFamily: "'Epilogue', sans-serif", transition: 'all 0.15s',
                background: activeTab === id
                    ? `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryLight} 100%)`
                    : 'transparent',
                color: activeTab === id ? '#fff' : C.onSurfaceVariant,
                boxShadow: activeTab === id ? `0 2px 8px ${C.primary}30` : 'none',
            }}
        >
            {label}
        </button>
    );
 
    const footerLinks = [
        { label: 'Privacy Policy',       href: '/privacy-policy' },
        { label: 'Community Guidelines', href: '/community-guidelines' },
        { label: 'Terms of Service',     href: '/terms' },
        { label: 'Contact Us',           href: '/contact' },
    ];
 
    return (
        <div style={{ minHeight: '100vh', background: C.surface, fontFamily: "'Plus Jakarta Sans', sans-serif", padding: '48px 24px 80px' }}>
            <div style={{ maxWidth: '760px', margin: '0 auto' }}>
 
                {/* Header */}
                <div style={{ marginBottom: '36px' }}>
                    <div style={{ display: 'inline-block', background: C.primaryContainer, color: C.primary, padding: '4px 14px', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', fontFamily: "'Epilogue', sans-serif", marginBottom: '16px' }}>
                        Safety
                    </div>
                    <h1 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '2.4rem', fontWeight: '900', color: C.onBackground, margin: '0 0 8px 0' }}>
                        Safety Center
                    </h1>
                    <p style={{ fontSize: '0.95rem', color: C.onSurfaceVariant, margin: 0, lineHeight: '1.6' }}>
                        Scriblish is built to be a safe, kind space for young writers. Here's how we keep it that way — and how you can help.
                    </p>
                </div>
 
                {/* Tab switcher */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '28px', background: '#fff', padding: '6px', borderRadius: '14px', border: `1px solid ${C.surfaceHigh}`, width: 'fit-content' }}>
                    {tabBtn('kids',    '👦 For Kids')}
                    {tabBtn('parents', '👨‍👩‍👧 For Parents')}
                </div>
 
                {/* ── KIDS TAB ─────────────────────────────────────────────── */}
                {activeTab === 'kids' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
 
                        {infoCard(
                            <>
                                <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>🛡️</div>
                                <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.3rem', fontWeight: '900', color: C.primary, margin: '0 0 8px 0' }}>You're Safe Here</h2>
                                {p("Scriblish is a place just for young writers aged 10–13. Every story is checked by our safety system and a real person before anyone else can read it — so you can focus on writing great stories.")}
                            </>,
                            C.primaryContainer, `${C.primary}30`
                        )}
 
                        {card(
                            <>
                                {h2('🚨 If Something Feels Wrong')}
                                {p("Trust your gut! If anything makes you feel uncomfortable, scared, or confused — here's what to do:")}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
                                    {[
                                        { step: '1', title: 'Stop and step away',   desc: "You don't have to read or respond to anything that makes you feel bad.", color: C.primaryContainer },
                                        { step: '2', title: 'Tell a trusted adult', desc: 'Show a parent, teacher, or grown-up you trust what you saw.',            color: C.secondaryContainer },
                                        { step: '3', title: 'Contact us directly',  desc: 'Email scriblishofficial@gmail.com — a real person will read your message and get back to you.', color: C.surfaceLow },
                                    ].map(item => (
                                        <div key={item.step} style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', background: item.color, borderRadius: '12px', padding: '16px' }}>
                                            <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: C.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '0.9rem', flexShrink: 0 }}>
                                                {item.step}
                                            </div>
                                            <div>
                                                <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '800', fontSize: '0.95rem', color: C.onBackground, marginBottom: '2px' }}>{item.title}</div>
                                                <div style={{ fontSize: '0.85rem', color: C.onSurfaceVariant, lineHeight: '1.5' }}>{item.desc}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
 
                        {card(
                            <>
                                {h2('🔍 How We Keep Scriblish Safe')}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                    {[
                                        { icon: '🤖', title: 'Smart Filters',        desc: 'Our system catches bad words and personal info the moment you try to publish.',       bg: C.primaryContainer },
                                        { icon: '👩‍💼', title: 'Real Moderators',     desc: 'A real adult checks every story before it goes live for other readers.',             bg: C.secondaryContainer },
                                        { icon: '🔒', title: 'No Personal Info',     desc: 'We never let anyone share real names, addresses, or contact details.',               bg: `${C.tertiaryContainer}80` },
                                        { icon: '✅', title: 'Checked Before Live',  desc: 'Nothing goes public until it passes both our safety filter and a human review.',     bg: C.surfaceLow },
                                    ].map(item => (
                                        <div key={item.title} style={{ background: item.bg, borderRadius: '14px', padding: '18px' }}>
                                            <div style={{ fontSize: '1.8rem', marginBottom: '8px' }}>{item.icon}</div>
                                            <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '800', fontSize: '0.9rem', color: C.onBackground, marginBottom: '4px' }}>{item.title}</div>
                                            <div style={{ fontSize: '0.8rem', color: C.onSurfaceVariant, lineHeight: '1.5' }}>{item.desc}</div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
 
                        {infoCard(
                            <>
                                <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', color: C.tertiary, margin: '0 0 10px 0', fontSize: '1.05rem' }}>📋 Remember the Golden Rules</h3>
                                {ul([
                                    li('Never share your real name, address, school, or phone number', 1),
                                    li('Never share your password — not even with friends', 2),
                                    li("Be kind to other writers, even if you don't love their story", 3),
                                    li('Tell an adult if anything online makes you feel unsafe', 4),
                                ])}
                            </>,
                            `${C.tertiaryContainer}80`, `${C.tertiary}30`
                        )}
                    </div>
                )}
 
                {/* ── PARENTS TAB ──────────────────────────────────────────── */}
                {activeTab === 'parents' && (
                    <div style={{ background: '#fff', borderRadius: '20px', padding: '48px', boxShadow: '0 4px 24px rgba(0,0,0,0.06)', border: `1px solid ${C.surfaceHigh}` }}>
 
                        {p("We take the safety of every child on Scriblish seriously. Here's what we do to protect your child — and how you can stay involved.")}
 
                        {/* Trust signal banner */}
                        {infoCard(
                            <>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                                    <div style={{ fontSize: '2rem' }}>✅</div>
                                    <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', color: C.secondary, fontSize: '1.1rem' }}>Every published story is safety-checked</div>
                                </div>
                                {p("No story on Scriblish reaches a public audience without passing both an automated safety filter and a human review. This is not optional — it is a requirement of the platform for every submission, every time.")}
                            </>,
                            C.secondaryContainer, `${C.secondary}30`
                        )}
 
                        {h2('How Content Moderation Works')}
                        {h3('Step 1 — Automated screening')}
                        {p('The moment a child submits a story for publishing, our safety filter runs automatically. It:')}
                        {ul([
                            li('Blocks personal identifying information (names, phone numbers, addresses, emails)', 1),
                            li('Flags profanity, violent language, and inappropriate themes using AI analysis', 2),
                            li('Detects attempts to share external links or contact details', 3),
                            li('Stops the submission immediately if anything is found, with a plain-language explanation shown to the writer', 4),
                        ])}
 
                        {h3('Step 2 — Human review')}
                        {ul([
                            li('Every story that clears the automated filter is then reviewed by a human moderator before going live', 1),
                            li('Moderators follow strict child-safety guidelines aligned with COPPA and industry best practices', 2),
                            li('A story that raises any concern during human review is held and not published until resolved', 3),
                        ])}
 
                        {h3('Enforcement')}
                        {p('Violations result in graduated responses — from educational warnings to account suspension or permanent removal. Parents are notified at every stage beyond a first-time minor violation.')}
 
                        {h2('Parent Controls & Supervision Tips')}
                        {h3('What you can control')}
                        {ul([
                            li('Review all stories and content your child has published', 1),
                            li('Set stories to private so only your child can see them', 2),
                            li('Request full deletion of your child\'s account and data at any time', 3),
                            li('Revoke publishing permissions so your child can write without sharing publicly', 4),
                        ])}
 
                        {h3('Tips for staying involved')}
                        {infoCard(
                            ul([
                                li("Read your child's stories together — it's a great conversation starter", 1),
                                li('Talk about what to do if something online feels wrong or uncomfortable', 2),
                                li('Make sure your child knows never to share personal information online', 3),
                                li('Check their account periodically and ask about what they\'re writing', 4),
                            ]),
                            C.primaryContainer, `${C.primary}20`
                        )}
 
                        {h2('Privacy & COPPA Compliance')}
                        {p(<>Scriblish is designed for writers aged 10–13 and is fully COPPA-compliant. We collect only the minimum data necessary, never sell or share children's data, and require verifiable parental consent before a child under 13 can create an account. See our <a href="/privacy-policy" style={{ color: C.primary, fontWeight: '700', textDecoration: 'none' }}>Privacy Policy</a> for full details.</>)}
 
                        {h2('Contact Us About a Safety Concern')}
                        {infoCard(
                            <>
                                <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '800', color: C.secondary, marginBottom: '12px', fontSize: '1rem' }}>🛡️ We're here to help</div>
                                {ul([
                                    li(<><strong>General safety:</strong> <a href="mailto:scriblishofficial@gmail.com" style={{ color: C.secondary, fontWeight: '700', textDecoration: 'none' }}>scriblishofficial@gmail.com</a></>, 1),
                                    li(<><strong>Response time:</strong> Within 48 hours for safety concerns</>, 2),
                                    li(<><strong>Emergency:</strong> Contact local law enforcement (911 in the US) first, then notify us</>, 3),
                                ])}
                            </>,
                            C.secondaryContainer, `${C.secondary}20`
                        )}
 
                        {/* Footer links */}
                        <div style={{ marginTop: '48px', paddingTop: '28px', borderTop: `1px solid ${C.surfaceHigh}`, display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'center' }}>
                            {footerLinks.map(({ label, href }) => (
                                <a key={label} href={href} style={{ color: C.primary, textDecoration: 'none', fontSize: '0.85rem', fontWeight: '600', fontFamily: "'Epilogue', sans-serif", transition: 'opacity 0.15s' }}
                                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.opacity = '0.7'; }}
                                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}
                                >
                                    {label}
                                </a>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
 
export default SafetyCenter;